import * as React from "react";
import { Box, Button, Container, Paper, Typography } from "@mui/material";
import { ContentProps } from "../../common/types";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { absoluteAssetUrl, canonicalUrl } from "../../common/seo";

interface Props {
  currPost: ContentProps;
}
const imageStyles = { width: "100%" };

export function Post(props: Props) {
  const location = useLocation();
  const pathname = location.pathname || `/${props.currPost.link}`;

  const title = `${props.currPost.title} | NWA Blog`;
  const description =
    props.currPost.subTitle ||
    "Northwest Arkansas trail and local spot photos and reviews.";

  const isParkLike =
    props.currPost.tags?.includes("trails") ||
    props.currPost.tags?.includes("fishing") ||
    props.currPost.category === "trails";

  const jsonLd = isParkLike
    ? {
        "@context": "https://schema.org",
        "@type": "Park",
        name: props.currPost.title,
        description: props.currPost.subTitle,
        url: canonicalUrl(pathname),
        image: props.currPost.ctaImage
          ? [absoluteAssetUrl(props.currPost.ctaImage)]
          : undefined,
      }
    : null;

  return (
    <Container component={"section"} sx={{ marginTop: "5rem" }}>
      <Box
        component={"section"}
        sx={{
          padding: "1rem",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <>
          <Helmet>
            <title>{title}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={canonicalUrl(pathname)} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:type" content="article" />
            <meta property="og:url" content={canonicalUrl(pathname)} />
            {props.currPost.ctaImage ? (
              <meta
                property="og:image"
                content={absoluteAssetUrl(props.currPost.ctaImage)}
              />
            ) : null}
            {jsonLd ? (
              <script type="application/ld+json">
                {JSON.stringify(jsonLd)}
              </script>
            ) : null}
          </Helmet>

          <Paper
            elevation={1}
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h5"
              align="left"
              sx={{ fontWeight: "bold", textAlign: "center" }}
            >
              {props.currPost.title}
            </Typography>
          </Paper>

          {isParkLike ? (
            <Typography variant="body2" sx={{ marginTop: ".5rem" }}>
              <Button
                component={RouterLink}
                to="/best-parks-lakes-northwest-arkansas"
                variant="outlined"
                size="small"
                sx={{
                  textTransform: "none",
                  color: "accent.main",
                  borderColor: "accent.main",
                  "&:hover": { borderColor: "accent.main" },
                }}
              >
                Back to Best Parks & Lakes
              </Button>
            </Typography>
          ) : null}

          {props.currPost.content.map((item, i) => {
            return item.path ? (
              <Paper
                alt={item.alt}
                component="img"
                src={`${item.path}`}
                sx={
                  item.styles ? { ...imageStyles, ...item.styles } : imageStyles
                }
                elevation={0}
                key={i}
              ></Paper>
            ) : (
              <Paper key={i} elevation={1} sx={{ padding: "1rem" }}>
                <Typography
                  variant="body2"
                  align="left"
                  sx={{
                    whiteSpace: "pre-line",
                  }}
                >
                  {item.text}
                </Typography>
              </Paper>
            );
          })}

          {isParkLike ? (
            <Typography variant="body2" sx={{ marginTop: "1rem" }}>
              <Button
                component={RouterLink}
                to="/best-parks-lakes-northwest-arkansas"
                variant="outlined"
                size="small"
                sx={{
                  textTransform: "none",
                  color: "accent.main",
                  borderColor: "accent.main",
                  "&:hover": { borderColor: "accent.main" },
                }}
              >
                Explore more parks & lakes
              </Button>
            </Typography>
          ) : null}
        </>
      </Box>
    </Container>
  );
}
